const RAZORPAY_RECOVERY_STORAGE_KEY =
  "onground:razorpay-recovery";

const cleanText = (value) => String(value || "").trim();

const normalizeSelectedItems = (value) =>
  Array.isArray(value)
    ? value
        .slice(0, 24)
        .map((item) => ({
          date: cleanText(item?.date),
          start: cleanText(item?.start),
          end: cleanText(item?.end),
          price:
            item?.price == null
              ? null
              : Number(item.price),
          discountedPrice:
            item?.discountedPrice == null
              ? null
              : Number(item.discountedPrice),
          currency: cleanText(item?.currency),
        }))
        .filter(
          (item) =>
            item.date && item.start && item.end,
        )
    : [];

const normalizeBookingSnapshot = (value = {}) => ({
  customer: {
    userName: cleanText(value?.customer?.userName),
    contactNumber: cleanText(
      value?.customer?.contactNumber,
    ),
    email: cleanText(value?.customer?.email),
  },
  phoneCountryCode: cleanText(
    value?.phoneCountryCode,
  ),
  selectedGroundId: cleanText(
    value?.selectedGroundId,
  ),
  selectedDate: cleanText(value?.selectedDate),
  selectedItems: normalizeSelectedItems(
    value?.selectedItems,
  ),
  promoCode: cleanText(value?.promoCode),
  promoPreview:
    value?.promoPreview &&
    typeof value.promoPreview === "object"
      ? {
          code: cleanText(
            value.promoPreview.code,
          ),
          description: cleanText(
            value.promoPreview.description,
          ),
          subtotal: Number(
            value.promoPreview.subtotal || 0,
          ),
          discountAmount: Number(
            value.promoPreview.discountAmount || 0,
          ),
          finalAmount: Number(
            value.promoPreview.finalAmount || 0,
          ),
        }
      : null,
});

const normalizePaymentOrder = (value = {}) => {
  const razorpayOrderId = cleanText(
    value?.razorpayOrderId,
  );
  const razorpayKeyId = cleanText(
    value?.razorpayKeyId,
  );
  const amount = Number(value?.amount || 0);
  const currency = cleanText(
    value?.currency || "INR",
  ).toUpperCase();

  if (
    !razorpayOrderId ||
    !razorpayKeyId ||
    !Number.isFinite(amount) ||
    amount <= 0
  ) {
    return null;
  }

  return {
    razorpayOrderId,
    razorpayKeyId,
    amount,
    currency,
    gateway: "RAZORPAY",
  };
};

const normalizeVerificationPayload = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const razorpayOrderId = cleanText(
    value.razorpayOrderId,
  );
  const razorpayPaymentId = cleanText(
    value.razorpayPaymentId,
  );
  const razorpaySignature = cleanText(
    value.razorpaySignature,
  );

  if (
    !razorpayOrderId ||
    !razorpayPaymentId ||
    !razorpaySignature
  ) {
    return null;
  }

  return {
    razorpayOrderId,
    razorpayPaymentId,
    razorpaySignature,
  };
};

const normalizeRecoveryContext = (value = {}) => {
  const slug = cleanText(value?.slug);
  const reservationId = cleanText(
    value?.reservationId,
  );
  const releaseToken = cleanText(
    value?.releaseToken,
  );
  const expiresAt = cleanText(value?.expiresAt);
  const paymentOrder = normalizePaymentOrder(
    value?.paymentOrder,
  );
  const requestedState = cleanText(
    value?.state,
  ).toLowerCase();
  const state = [
    "preparing",
    "ready",
    "failed",
    "pending",
  ].includes(requestedState)
    ? requestedState
    : "ready";

  if (
    !slug ||
    !reservationId ||
    !releaseToken ||
    (state !== "preparing" && !paymentOrder)
  ) {
    return null;
  }

  const startedAt = Number(
    value?.startedAt || Date.now(),
  );

  return {
    slug,
    reservationId,
    releaseToken,
    expiresAt,
    state,
    startedAt: Number.isFinite(startedAt)
      ? startedAt
      : Date.now(),
    paymentOrder,
    verificationPayload:
      normalizeVerificationPayload(
        value?.verificationPayload,
      ),
    bookingSnapshot:
      normalizeBookingSnapshot(
        value?.bookingSnapshot,
      ),
  };
};

export const saveRazorpayRecoveryContext = (
  storage,
  value,
) => {
  if (!storage) return false;

  const normalized = normalizeRecoveryContext(value);

  if (!normalized) return false;

  try {
    storage.setItem(
      RAZORPAY_RECOVERY_STORAGE_KEY,
      JSON.stringify(normalized),
    );
    return true;
  } catch {
    return false;
  }
};

export const getRazorpayRecoveryContext = (
  storage,
) => {
  if (!storage) return null;

  try {
    const raw = storage.getItem(
      RAZORPAY_RECOVERY_STORAGE_KEY,
    );

    if (!raw) return null;

    return normalizeRecoveryContext(
      JSON.parse(raw),
    );
  } catch {
    return null;
  }
};

export const getRazorpayRecoveryResumeAction = (
  value,
) => {
  if (!value || typeof value !== "object") {
    return "none";
  }

  if (value.verificationPayload) {
    return "verify";
  }

  const state = cleanText(value.state).toLowerCase();

  if (state === "failed") {
    return "abandon";
  }

  if (state === "pending") {
    return "wait";
  }

  return "resume";
};

export const clearRazorpayRecoveryContext = (
  storage,
) => {
  if (!storage) return false;

  try {
    storage.removeItem(
      RAZORPAY_RECOVERY_STORAGE_KEY,
    );
    return true;
  } catch {
    return false;
  }
};

export const buildRazorpayResultSearch = ({
  resultType,
  bookingReference = "",
  groundName = "",
  bookingWhen = "",
} = {}) => {
  const normalizedResult = cleanText(
    resultType,
  ).toLowerCase();

  if (
    !["success", "pending", "failed", "error"].includes(
      normalizedResult,
    )
  ) {
    return "";
  }

  const params = new URLSearchParams();
  params.set("razorpay_payment", normalizedResult);
  params.set("payment_gateway", "razorpay");

  if (cleanText(bookingReference)) {
    params.set(
      "booking_reference",
      cleanText(bookingReference).slice(0, 80),
    );
  }

  if (cleanText(groundName)) {
    params.set(
      "booking_ground",
      cleanText(groundName).slice(0, 120),
    );
  }

  if (cleanText(bookingWhen)) {
    params.set(
      "booking_when",
      cleanText(bookingWhen).slice(0, 200),
    );
  }

  return `?${params.toString()}`;
};
